import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Button } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import './Navigation.scss';

const Logo = `${process.env.PUBLIC_URL}/images/Logo1.png`;

/**
 * Navigation component for MET Connect® website
 * 
 * Provides responsive navigation with scrolling effects and mobile menu support
 * 
 * @returns {JSX.Element} The Navigation component
 */
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for navbar transparency control on scroll
  const [isTransparent, setIsTransparent] = useState(true);
  // State for mobile menu toggling
  const [expanded, setExpanded] = useState(false);

  /**
   * Handle scroll event to change navbar appearance on scroll
   */
  useEffect(() => {
    const handleScroll = () => {
      // Change navbar style when scrolled past 100px
      if (window.scrollY > 100) {
        setIsTransparent(false);
      } else {
        setIsTransparent(true);
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  /**
   * Toggle mobile menu expanded state
   */
  const toggleMenu = () => {
    setExpanded(!expanded);
  };

  /**
   * Close mobile menu
   */
  const closeMenu = () => {
    setExpanded(false);
  };

  /**
   * Scroll to the top of the page when home button is clicked
   */
  const scrollToTop = () => {
    closeMenu();
    if (location.pathname === '/') {
      // If already on homepage, just scroll to the top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // If not on homepage, navigate to the home page
      navigate('/');
    }
  };
  
  /**
   * Scroll to a section when clicking a nav link
   * @param {string} sectionId - The ID of the section to scroll to
   */
  const scrollToSection = (sectionId) => {
    // Close the mobile menu
    closeMenu();
    
    // Check if we're on the homepage
    if (location.pathname === '/') {
      // If on homepage, scroll to the section
      const section = document.getElementById(sectionId);
      if (section) {
        // The CSS scroll-margin-top handles the offset
        section.scrollIntoView({ 
          behavior: 'smooth'
        });
      }
    } else {
      // If not on homepage, navigate to homepage with scrollTo state
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  /**
   * Handle login button click to open billing portal in new tab
   */
  const handleLoginClick = () => {
    closeMenu();
    window.open('https://portal.met-connect.com/', '_blank');
  };

  return (
    <>
      <Navbar 
        expand="lg" 
        fixed="top"
        expanded={expanded}
        className={`main-navbar ${isTransparent ? 'navbar-transparent' : 'navbar-solid'}`}
      >
        <Container>
          <Navbar.Brand as={Link} to="/" onClick={scrollToTop} className="d-flex align-items-center">
            <img src={Logo} alt="MET Connect® Logo" className="navbar-logo me-3" />
            <div className="text-logo">
              <div className="logo-main-text">MET Connect<sup>®</sup></div>
              <div className="logo-sub-text">Connected Since 2010</div>
            </div>
          </Navbar.Brand>
          
          <Navbar.Toggle 
            aria-controls="basic-navbar-nav" 
            onClick={toggleMenu}
          />
          
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link onClick={scrollToTop}>Home</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('services')}>Services</Nav.Link>
              <Nav.Link as={Link} to="/resources" onClick={closeMenu}>Resources</Nav.Link>
              <Nav.Link as={Link} to="/support" onClick={closeMenu}>Support</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('about')}>About Us</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('contact')}>Contact Us</Nav.Link>
            </Nav>
            
            <div className="nav-buttons">
              <Button 
                variant="outline-light" 
                className="animated-btn login-btn"
                onClick={handleLoginClick}
              >
                <span className="animated-btn-content">
                  Login
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </span>
              </Button>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
};

export default Navigation;